import { useEffect, useRef } from 'react';
import type { PlayMode, Game } from 'app/CasinoGames/types';
import gamePath from './gamePath';

export type GameHistoryEntry = {
  push: (game: Game, mode: PlayMode) => void;
  setMode: (mode: PlayMode) => void;
  leave: () => Promise<void>;
};

const withMode = (mode: PlayMode) => {
  const url = new URL(window.location.href);
  url.searchParams.set('mode', mode);

  return url;
};

// `game.slug` is assumed present: the `/game/:vendor/:slug` route only ships once every
// CasinoGame row has been backfilled with one (see casino_games:backfill_slugs).
const gameUrl = (game: Game, mode: PlayMode) => {
  const url = withMode(mode);
  url.pathname = gamePath(game) as string;

  return url;
};

type Deferred = { promise: Promise<void>; resolve: () => void };

const deferred = (): Deferred => {
  let resolve = () => {};
  const promise = new Promise<void>((r) => {
    resolve = r;
  });

  return { promise, resolve };
};

// The history entry of a game opened from the grid: pushed over the list's, so Back closes the game
// (`onBack`) and returns to the list. A /game/:vendor/:slug page is a document of its own, with no
// entry pushed.
// `onBack` is the first render's: it is called from a listener added once.
// eslint-disable-next-line max-lines-per-function
const useGameHistoryEntry = (onBack: () => void): GameHistoryEntry => {
  const pushed = useRef(false);
  // The Back of leave, until the browser is done with it.
  const leaving = useRef<Deferred | null>(null);

  const push = (game: Game, mode: PlayMode) => {
    pushed.current = true;
    window.history.pushState({}, '', gameUrl(game, mode));
  };

  const setMode = (mode: PlayMode) => {
    window.history.replaceState({}, '', withMode(mode));
  };

  // Resolves once the game's entry, if pushed, is left with Back: so a search that replaceStates the
  // list's address then replaces the list's entry, not the game's - the history keeps no closed
  // games, and Back from the new list leaves the page, as from any list, instead of showing the old
  // list's address over the new results. Called again before that Back is done, it waits for the
  // same one: a second Back would leave the list too.
  const leave = (): Promise<void> => {
    if (pushed.current && !leaving.current) {
      leaving.current = deferred();
      window.history.back();
    }

    return leaving.current?.promise ?? Promise.resolve();
  };

  useEffect(() => {
    // Back from the game's entry, by the visitor or by leave.
    const onPopState = () => {
      pushed.current = false;
      onBack();

      const left = leaving.current;
      // In a task of its own: the browser brings back the list's scroll position right after
      // popstate, over whatever is done on leave (e.g. a scroll to the top for new results). Kept
      // pending till then, so a leave called meanwhile waits for it too, in turn.
      if (left) {
        window.setTimeout(() => {
          leaving.current = null;
          left.resolve();
        });
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  return { push, setMode, leave };
};

export default useGameHistoryEntry;
